<template>
  <div class="card" style="width: 100%">
    <div class="card-header">{{ label }}</div>
    <div class="card-body">
      <div class="card-text col-md-12">
        <DerToggleWithLabel
          :disabled="!center.id || !center.active"
          :model-value="features[featureId].active"
          :label="label"
          @update:model-value="toggleFeature"
        />
      </div>

      <div v-if="features[featureId].active" class="mt-4">
        <div v-if="!editMode">
          <div class="mb-2">
            <strong>Client ID:</strong> {{ config.client_id || "-" }}
          </div>
          <div class="mb-2">
            <strong>Clinic:</strong> {{ config.clinic_label || "-" }}
          </div>
          <div class="mb-2">
            <strong>Document Type:</strong>
            {{ config.document_type_label || "-" }}
          </div>
          <button class="btn btn-sm btn-outline-primary" @click="openEditMode">
            Edit
          </button>
        </div>

        <div v-else>
          <div class="mb-3">
            <label>Client ID <i class="far fa-asterisk" /></label>
            <input
              v-model="config.client_id"
              type="text"
              class="form-control"
              :disabled="loading"
            />
          </div>
          <div class="mb-3">
            <label>Client Secret <i class="far fa-asterisk" /></label>
            <div class="input-group">
              <input
                v-model="config.client_secret"
                :type="showSecret ? 'text' : 'password'"
                class="form-control"
                :disabled="loading"
              />
              <div class="input-group-append">
                <button
                  type="button"
                  class="btn btn-default"
                  @click="showSecret = !showSecret"
                >
                  <i :class="showSecret ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger">{{ error }}</div>

          <div class="mb-3">
            <label>Clinic <i class="far fa-asterisk" /></label>
            <select
              v-model="config.clinic_id"
              class="form-control"
              :disabled="!clinics.length || loading"
              @change="updateClinicLabel"
            >
              <option
                v-for="clinic in clinics"
                :key="clinic.id"
                :value="clinic.id"
              >
                {{ clinic.name }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label>Document Type <i class="far fa-asterisk" /></label>
            <select
              v-model="config.document_type"
              class="form-control"
              :disabled="!documentTypes.length || loading"
              @change="updateDocTypeLabel"
            >
              <option
                v-for="type in documentTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-secondary"
              :disabled="!config.client_id || !config.client_secret || loading"
              @click="fetchWebdocData"
            >
              {{ loading ? "Loading" : "Update from Webdoc" }}
            </button>
            <button class="btn btn-outline-secondary" @click="editMode = false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DerToggleWithLabel from "../../design-system/DerToggleWithLabel.vue";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";

export default {
  components: { DerToggleWithLabel },
  props: {
    center: Object,
    features: Object,
    featureId: Number,
    label: String,
  },
  data() {
    return {
      clinics: [],
      documentTypes: [],
      loading: false,
      error: null,
      editMode: false,
      showSecret: false,
    };
  },
  computed: {
    config() {
      return this.features[this.featureId].config;
    },
  },
  methods: {
    toggleFeature(value) {
      this.features[this.featureId].active = value;

      if (value) {
        this.openEditMode();
      }
    },
    updateClinicLabel() {
      const selected = this.clinics.find((c) => c.id === this.config.clinic_id);
      this.config.clinic_label = selected ? selected.name : "";
    },
    updateDocTypeLabel() {
      const selected = this.documentTypes.find(
        (t) => t.id === this.config.document_type,
      );
      this.config.document_type_label = selected ? selected.name : "";
    },
    async fetchWebdocData() {
      if (!this.config.client_id || !this.config.client_secret) return;

      this.loading = true;
      this.error = null;
      try {
        const params = {
          client_id: this.config.client_id,
          client_secret: this.config.client_secret,
        };

        const [clinicsRes, docTypesRes] = await Promise.all([
          practitionerHttpService.post(
            `/api/internal/admin/centers/webdoc/clinics`,
            params,
          ),
          practitionerHttpService.post(
            `/api/internal/admin/centers/webdoc/document-types`,
            params,
          ),
        ]);

        this.clinics = clinicsRes.data;
        this.documentTypes = docTypesRes.data;

        this.updateClinicLabel();
        this.updateDocTypeLabel();
      } catch (e) {
        this.error = "Failed to fetch data from Webdoc";
        this.clinics = [];
        this.documentTypes = [];
      } finally {
        this.loading = false;
      }
    },
    async openEditMode() {
      this.editMode = true;
      await this.fetchWebdocData();
    },
  },
};
</script>
